import type {
    FC
} from "react";
import {
    Pressable,
    Animated,
    Platform
} from "react-native";
import type INumericInputStepButtonProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePressScale
} from "../../../../helpers/motion";
import type {
    NumericInputStepDirection
} from "../../type";
import {
    Minus as MinusIcon,
    Plus as PlusIcon,
    type LucideIcon
} from "lucide-react-native";
import {
    getNumericInputStepColors
} from "../../stylesheet";
import AnimatedIcon from "../../../animatedIcon";
import StateLayer from "../../../stateLayer";

const NumericInputStepIcon: Record<NumericInputStepDirection, LucideIcon> = {
    decrement: MinusIcon,
    increment: PlusIcon
};

const preventStepFocusSteal = (event: {
    preventDefault: () => void;
}) => {
    event.preventDefault();
};

const NumericInputStepButton: FC<INumericInputStepButtonProps> = ({
    accessibilityLabel,
    customTheme,
    stepperSize,
    controlsID,
    isDisabled,
    direction,
    iconSize,
    onPress
}) => {
    const {
        shapeConfigs,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled
    });

    const pressScaleStyle = usePressScale({
        customTheme,
        isDisabled,
        isPressed
    });

    const currentColors = getNumericInputStepColors({
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        isFocusVisible,
        shapeConfigs,
        stepperSize,
        direction,
        borders,
        colors
    });

    const StepIcon = NumericInputStepIcon[direction];

    const webProps = Platform.OS === "web" ? {
        onMouseDown: preventStepFocusSteal,
        "aria-controls": controlsID
    } : {};

    return <Pressable
        {...webProps}
        {...interactionHandlers}
        tabIndex={Platform.OS === "web" ? -1 : undefined}
        accessibilityLabel={accessibilityLabel}
        style={[
            stylesheet.stepContainer,
            containerDynamicStyle
        ]}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        onPress={onPress}
        role="button"
    >
        <Animated.View
            style={[
                stylesheet.stepSurface,
                surfaceDynamicStyle,
                pressScaleStyle
            ]}
        >
            <StateLayer
                borderRadius={stepperSize.radius}
                customTheme={customTheme}
                overlayColor="default"
                isPressed={isPressed}
                isHovered={isHovered}
                isSquircle={true}
            />
            <AnimatedIcon
                customColor={currentColors.iconColor}
                animationTrigger="hover"
                animation="signature"
                icon={StepIcon}
                size={iconSize}
            />
        </Animated.View>
    </Pressable>;
};
export default NumericInputStepButton;
